import { useState } from 'react';
import { Box, Grid, TextField, Button, Typography, Select, MenuItem, FormControl, InputLabel, Paper, Alert, Snackbar, CircularProgress, Divider } from '@mui/material';
import { GetApp } from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { useLocation } from "react-router-dom";
import axios from "axios";
import { useEffect } from "react";

const toTitleCase = (str = "") =>
    str
        .toLowerCase()
        .split(" ")
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");

const formatDateToDDMMYYYY = (dateStr = "") => {
    if (!dateStr) return "";
    const [yyyy, mm, dd] = dateStr.split("-");
    return `${dd}/${mm}/${yyyy}`;
};

const limitTo120Chars = (value = "") =>
    String(value).length > 120 ? String(value).slice(0, 120) : String(value);

const splitAddress120 = (address = "") => {
    // Line breaks (\r, \n) in an address are replaced with a space
    const str = String(address).replace(/[\r\n]+/g, " ");
    return {
        addr1: str.slice(0, 100),
        addr2: str.length > 100 ? str.slice(100, 200) : ""
    };
};

const base64ToBlob = (base64, type = 'application/octet-stream') => {
    const byteCharacters = atob(base64);
    const byteNumbers = new Array(byteCharacters.length);

    for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
    }

    const byteArray = new Uint8Array(byteNumbers);
    return new Blob([byteArray], { type: type });
};

const EwayBillMaster = ({ connectionString, invoiceId, bookName}) => {
    const { user, selectedCompany } = useAuth();
    const companyId = selectedCompany?.CompId || null;
    // const location = useLocation();
    // const invoiceId = location.state?.invoiceId || null;
    // const bookName = location.state?.bookName || null;
    const [formState, setFormState] = useState({ EWBUserName: '', EWBPassword: '', partyName: '', billNo: '', ewayBillNo: '', supplyType: 'Outward', subType: 'Supply', docType: 'Tax Invoice', transMode: 'Road', dispatchState: 'Combo1', shipToState: 'Combo1', vehicleType: 'Regular', distance: '', transType: 'Regular', });
    const [errors, setErrors] = useState({});
    const [submitError, setSubmitError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [ewayBillLoading, setEwayBillLoading] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [stateList, setStateList] = useState([]);
    const [ewayData, setEwayData] = useState(null);
    const [vehicleValidationErrors, setVehicleValidationErrors] = useState({});
    const [specValidationIssues, setSpecValidationIssues] = useState([]);
    const [ewayBillError, setEwayBillError] = useState('');
    const [ewayBillSuccess, setEwayBillSuccess] = useState(false);
    const [ewayExists, setEwayExists] = useState(false);
    const [ewayBillServer, setEwayBillServer] = useState('Primary');

    const mode = "EWB";

    const validateVehicleDetails = (transporterName, transporterId, vehicleNo) => {
        const errors = {};

        if (transporterName === "SELF" || !transporterId?.trim()) {
            if (!vehicleNo?.trim()) {
                errors.vehicleNo = "Vehicle No is required when transporter is SELF or not specified";
            }
            else if (!/^[A-Z0-9]+$/.test(vehicleNo.trim())) {
                errors.vehicleNo = "Vehicle No should only contain letters and numbers (e.g., 24R44RFRJC)";
            }
        }
        else {
            if (vehicleNo?.trim() && !/^[A-Z0-9]+$/.test(vehicleNo.trim())) {
                errors.vehicleNo = "Vehicle No should only contain letters and numbers";
            }
        }

        return errors;
    };


    useEffect(() => {
        if (!companyId || !invoiceId || !bookName) return;

        const checkEwayExists = async () => {
            try {
                const res = await axios.get(
                    `${connectionString}/api/check-eway-exists`,
                    {
                        params: {
                            companyId,
                            bookName,
                            invoiceId
                        }
                    }
                );

                if (res.data?.success && res.data?.exists) {

                    // ✅ Set EWB number in form
                    setFormState(prev => ({
                        ...prev,
                        ewayBillNo: res.data.data.ewayBillNo || ""
                    }));

                    setEwayExists(true);
                    setEwayBillSuccess(true); // Important for download button

                } else {
                    setEwayExists(false);
                }

            } catch (error) {
                console.error("Check EWB Exists Error:", error);
            }
        };

        checkEwayExists();

    }, [companyId, invoiceId, bookName]);

    useEffect(() => {
        if (!companyId) return;

        const fetchCompanyCredentials = async () => {
            try {
                const res = await axios.get(
                    `${connectionString}/api/ewaybill/getCompanyUserNameandPassword`, { params: { companyId, mode: "EWB" } });
                console.log(res);

                if (res.data?.success) {
                    setFormState(prev => ({
                        ...prev,
                        EWBUserName: res.data.data.username || "",
                        EWBPassword: res.data.data.password || "",
                        gstNo: res.data.data.gstNo
                    }));
                }
            } catch (err) {
                console.error("Failed to load company credentials", err);
            }
        };

        fetchCompanyCredentials();
    }, [companyId]);

    const getTransTypeByPincode = (data) => {
        const companyPin = Number(data?.company?.pincode || "");
        const shipFromPin = Number(data?.consigner?.shipFrom?.pincode || data?.company?.pincode || "");
        const clientPin = Number(data?.client?.details?.pincode || "");
        const shipToPin = Number(data?.consigner?.shipTo?.pincode || data?.client?.details?.pincode || "");

        if (companyPin === shipFromPin && clientPin === shipToPin) {
            return "Regular";
        }
        if (companyPin === shipFromPin && clientPin !== shipToPin) {
            return "Bill To-Ship To";
        }
        if (companyPin !== shipFromPin && clientPin === shipToPin) {
            return "Bill From-Disaptch From";
        }
        if (companyPin !== shipFromPin && shipToPin !== clientPin) {
            return "Combination of 2 and 3";
        }
        return "Regular";
    };

    const isCompanyFrom = formState.transType === "Regular" || formState.transType === "Bill To-Ship To";
    const fromAddressObj = splitAddress120(ewayData?.company?.address || "");
    const fromPlace = isCompanyFrom ? String(ewayData?.company?.city || "") : String(ewayData?.consigner?.shipFrom?.City || "");
    const fromPincode = isCompanyFrom ? Number(ewayData?.company?.pincode || "") : Number(ewayData?.consigner?.shipFrom?.pincode || "");
    const actFromStateCode = isCompanyFrom ? Number(ewayData?.company?.stateCode || "") : Number(ewayData?.consigner?.shipFrom?.stateCode || ewayData?.company?.stateCode || "");
    const dispatchFromGSTIN = !isCompanyFrom ? String(ewayData?.company?.gstNo || "URP") : String(ewayData?.consigner?.shipFrom?.gstno || "");
    const dispatchFromTradeName = !isCompanyFrom ? String(ewayData?.company?.companyName || "") : String(ewayData?.consigner?.shipFrom?.clientname || "");

    const isClientTo = formState.transType === "Regular" || formState.transType === "Bill From-Disaptch From";
    const toAddressObj = isClientTo ? splitAddress120(ewayData?.client?.details?.address || "") : splitAddress120(ewayData?.consigner?.shipTo?.Address || "");
    const toAddr1 = toAddressObj.addr1;
    const toAddr2 = toAddressObj.addr2;
    const toPlace = isClientTo ? String(ewayData?.client?.details?.City || "") : String(ewayData?.consigner?.shipTo?.City || "");
    const toPincode = isClientTo ? Number(ewayData?.client?.details?.pincode || "") : Number(ewayData?.consigner?.shipTo?.pincode || "");
    const actToStateCode = isClientTo ? Number(ewayData?.client?.details?.stateCode || "") : Number(ewayData?.consigner?.shipTo?.stateCode || ewayData?.client?.details?.stateCode || "");
    const shipToGSTIN = isClientTo ? String(ewayData?.client?.details?.["GST No."] || "URP") : String(ewayData?.consigner?.shipTo?.gstno || "URP");
    const shipToTradeName = isClientTo ? String(ewayData?.client?.partyName || "") : String(ewayData?.consigner?.shipTo?.clientname || "");

    // let transMode;
    // let VehicleNo;
    // if (VehicleNo === "") {
    //     return transMode = null;
    // }


    useEffect(() => {
        if (!invoiceId || !bookName) return;
        fetchEwayBillData(invoiceId, bookName);
    }, [invoiceId, bookName])

    useEffect(() => {
        if (!companyId) return;

        const fetchStates = async () => {
            try {
                const res = await axios.get(
                    `${connectionString}/api/states`,
                    { params: { companyId } }
                );
                setStateList(res.data);
            } catch (err) {
                console.error("State fetch failed", err);
            }
        };

        fetchStates();
    }, [companyId]);

    // Validation hook: runs when ewayData loads to check all required conditions
    useEffect(() => {
        if (!ewayData) return;

        const pageLoadErrors = [];
        const vehicleErrors = {};

        // 1. Client Pincode is required
        const clientPincode = ewayData?.client?.details?.pincode;
        if (!clientPincode || String(clientPincode).trim() === "") {
            pageLoadErrors.push("Client Pincode is required");
        }

        // 2. ToPincode is required and must be 6 digits
        if (!toPincode || !/^\d{6}$/.test(String(toPincode))) {
            pageLoadErrors.push("To Pincode is required and must be a valid 6-digit pincode");
        }

        // 3. Get distance based on formState.transType
        let distanceToCheck = formState.distance;
        if (formState.transType === "Regular" || formState.transType === "Bill From-Disaptch From") {
            distanceToCheck = ewayData?.client?.details?.Distance || "";
        } else {
            distanceToCheck = ewayData?.consigner?.shipTo?.Distance || "";
        }

        // 4. If FromPincode and ToPincode are same, distance must be > 0
        if (fromPincode && toPincode && Number(fromPincode) === Number(toPincode)) {
            if (!distanceToCheck || Number(distanceToCheck) <= 0) {
                pageLoadErrors.push("Distance must be greater than 0 when From and To pincodes are the same");
            }
        }

        // 5. TransporterName is required
        const transporterName = ewayData?.transporter?.transporterName || "";
        if (!transporterName?.trim()) {
            pageLoadErrors.push("Transporter Name is required");
        }

        // 6. Vehicle No validation: required if TransporterGSTNo is null OR TransporterName is SELF
        const transporterGstNo = ewayData?.transporter?.transporterGstNo || "";
        const vehicleNo = ewayData?.vehicleNo || "";

        if (!transporterGstNo?.trim() || transporterName === "SELF") {
            if (!vehicleNo?.trim()) {
                vehicleErrors.vehicleNo = "Vehicle No is required when Transporter GST No is null or Transporter Name is SELF";
                pageLoadErrors.push("Vehicle No is required when Transporter GST No is null or Transporter Name is SELF");
            }
            // Vehicle No format: continuous alphanumeric only (e.g., GJ26JD5356), no spaces or special chars
            else if (!/^[A-Z0-9]+$/.test(vehicleNo.trim())) {
                vehicleErrors.vehicleNo = "Vehicle No should be continuous alphanumeric only (e.g., GJ26JD5356), no spaces or special characters";
                pageLoadErrors.push("Vehicle No format is invalid: should be continuous alphanumeric only (e.g., GJ26JD5356)");
            }
        } else {
            // Transporter GST exists and is not SELF: vehicle No is optional but if provided, must be alphanumeric only
            if (vehicleNo?.trim() && !/^[A-Z0-9]+$/.test(vehicleNo.trim())) {
                vehicleErrors.vehicleNo = "Vehicle No should be continuous alphanumeric only (e.g., GJ26JD5356), no spaces or special characters";
                pageLoadErrors.push("Vehicle No format is invalid: should be continuous alphanumeric only (e.g., GJ26JD5356)");
            }
        }

        setVehicleValidationErrors(vehicleErrors);

        if (pageLoadErrors.length > 0) {
            setSubmitError(pageLoadErrors.map((err) => `• ${err}`).join('\n'));
        } else {
            setSubmitError('');
        }
    }, [ewayData, formState.distance, formState.transType]);

    const fetchEwayBillData = async (id, bookName) => {
        try {
            setIsLoading(true);

            const endpoint =
                bookName === 'SALE'
                    ? `${connectionString}/api/ewaybill/by-invoice/${id}`
                    : bookName === 'SACH'
                        ? `${connectionString}/api/ewaybill/by-saleChallan/${id}`
                        : bookName === 'CRNT'
                            ? `${connectionString}/api/ewaybill/by-creditNote/${id}`
                            : null;
            const { data } = await axios.get(endpoint, { params: { companyId, bookName } });

            console.log("data:", data);

            setEwayData(data);

            const transType = getTransTypeByPincode(data);
            let distanceValue = "";
            if (transType === "Regular" || transType === "Bill From-Disaptch From") {
                distanceValue = data?.client?.details?.Distance || "";
            } else {
                distanceValue = data?.consigner?.shipTo?.Distance || "";
            }

            setFormState(prev => ({
                ...prev,
                partyName: data?.client?.partyName || "",
                billNo: data?.client?.BillNumber || "",
                transType: transType,
                docType:
                    bookName === "SALE"
                        ? "Tax Invoice"
                        : bookName === "SACH"
                            ? "Delivery Challan"
                            : bookName === "CRNT"
                                ? "Credit Note"
                                : prev.docType,
                supplyType: bookName === "SALE" || bookName === "SACH"
                    ? "Outward"
                    : bookName === "CRNT"
                        ? "Inward"
                        : prev.supplyType,
                subType: data?.subType || prev.subType,
                transMode: data?.transMode || prev.transMode,
                vehicleType: data?.vehicleType || prev.vehicleType,
                dispatchState: data?.client?.details?.State ? toTitleCase(data.client.details.State) : prev.dispatchState,
                shipToState: data?.client?.details?.State ? toTitleCase(data.client.details.State) : prev.shipToState,
                distance: distanceValue,
            }));

        } catch (err) {
            console.error(err);
            setSnackbar({
                open: true,
                severity: "error",
                message: "Failed to load invoice data"
            });
        } finally {
            setIsLoading(false);
        }
    };

    const supplyTypeOptions = ['Inward', 'Outward'];
    const subTypeOptions = ['Supply', 'Import', 'Export', 'Job Work', 'For Own Use', 'Job work Returns', 'Sales Return', 'Others', 'SKD/CKD/Lots', 'Line Sales', 'Recipient Not Known', 'Exhibition or Fairs',];
    const docTypeOptions = ['Tax Invoice', 'Bill of Supply', 'Bill of Entry', 'Delivery Challan', 'Credit Note', 'Others',];
    const transModeOptions = ['Road', 'Rail', 'Air', 'Ship / Road cum Ship'];
    const vehicleTypeOptions = ['Regular', 'ODC'];
    const transTypeOptions = ['Regular', 'Bill To-Ship To', 'Bill From-Disaptch From', 'Combination of 2 and 3'];

    const STATE_OPTIONS = stateList.map(s => toTitleCase(s.state));

    const supplyTypeMap = { Inward: 'I', Outward: 'O', };
    const subTypeMap = { Supply: 1, Import: 2, Export: 3, 'Job Work': 4, 'For Own Use': 5, 'Job work Returns': 6, 'Sales Return': 7, Others: 8, 'SKD/CKD/Lots': 9, 'Line Sales': 10, 'Recipient Not Known': 11, 'Exhibition or Fairs': 12, };
    const docTypeMap = { 'Tax Invoice': 'INV', 'Bill of Supply': 'BIL', 'Bill of Entry': 'BOE', 'Delivery Challan': 'CHL', 'Credit Note': 'CNT', Others: 'OTH', };
    const transModeMap = { Road: 1, Rail: 2, Air: 3, 'Ship / Road cum Ship': 4, };
    const vehicleTypeMap = { Regular: 'R', ODC: 'O', };
    const transTypeMap = { Regular: 1, 'Bill To-Ship To': 2, 'Bill From-Disaptch From': 3, 'Combination of 2 and 3': 4, };

    const validate = () => {
        const newErrors = {};
        if (!formState.partyName?.trim()) newErrors.partyName = 'Party Name is required';
        if (!formState.billNo?.trim()) newErrors.billNo = 'Bill No is required';
        if (!formState.EWBUserName?.trim()) newErrors.EWBUserName = 'EWB Username is required';
        if (!formState.EWBPassword?.trim()) newErrors.EWBPassword = 'EWB Password is required';
        setErrors(newErrors);
        return newErrors;
    };

    // ═══════════════════════════════════════════════════════════════════
    // E-WAY BILL SPEC VALIDATION (only the rules requested):
    // - HSN code: 4 or 8 digits
    // - Unit: max 3 characters
    // - GSTIN (if present): must be 15 characters
    // - From_Address1 / From_Address2 / From_Place: alphanumeric + @ # - / & ,
    // - From_OtherPartyName / To_OtherPartyName: alphanumeric + - (hyphen) / (slash) . (dot)
    // - Dispatch_Pin Code: 6-digit, must belong to Dispatch From State
    // - Product / Description: alphanumeric + @ # - / & ,
    // - Assessable Value: required, numeric
    // ═══════════════════════════════════════════════════════════════════
    const ADDRESS_CHARSET_REGEX = /^[a-zA-Z0-9~!@#$%^&*()_\-+=\\;:'\".\/, ]*$/;
    const NAME_CHARSET_REGEX = /^[a-zA-Z0-9\-\/. ]*$/;
    const PRODUCT_CHARSET_REGEX = /^[a-zA-Z0-9\-\/. ]*$/;

    const validateEwaySpecFields = (payload) => {
        const issues = [];

        // GSTIN — if present, must be 15 characters
        if (payload.fromGstin && payload.fromGstin !== "URP" && payload.fromGstin.length !== 15) {
            issues.push(`From GSTIN "${payload.fromGstin}" must be 15 characters`);
        }
        if (payload.toGstin && payload.toGstin !== "URP" && payload.toGstin.length !== 15) {
            issues.push(`To GSTIN "${payload.toGstin}" must be 15 characters`);
        }

        // From_Address1 / From_Address2 / From_Place — allowed charset
        if (payload.fromAddr1 && !ADDRESS_CHARSET_REGEX.test(payload.fromAddr1)) {
            issues.push(`From Address1 contains invalid characters (only alphanumeric and the allowed special characters)`);
        }
        if (payload.fromAddr2 && !ADDRESS_CHARSET_REGEX.test(payload.fromAddr2)) {
            issues.push(`From Address2 contains invalid characters (only alphanumeric and the allowed special characters)`);
        }
        if (payload.fromPlace && !ADDRESS_CHARSET_REGEX.test(payload.fromPlace)) {
            issues.push(`From Place contains invalid characters (only alphanumeric and the allowed special characters)`);
        }

        // From_OtherPartyName — allowed charset
        if (payload.fromTrdName && !NAME_CHARSET_REGEX.test(payload.fromTrdName)) {
            issues.push(`From Party Name contains invalid characters (only alphanumeric and - / . allowed)`);
        }

        // To_OtherPartyName — allowed charset
        if (payload.toTrdName && !NAME_CHARSET_REGEX.test(payload.toTrdName)) {
            issues.push(`To Party Name contains invalid characters (only alphanumeric and - / . allowed)`);
        }

        // Dispatch_Pin Code — 6-digit, must belong to Dispatch From State
        if (!payload.fromPincode || !/^\d{6}$/.test(String(payload.fromPincode))) {
            issues.push(`Dispatch Pincode "${payload.fromPincode}" must be a valid 6-digit pincode`);
        } else if (stateList.length > 0) {
            const dispatchStateEntry = stateList.find(
                (s) => toTitleCase(s.state) === formState.dispatchState
            );
        }

        // Shipment_Pin Code — 6-digit, must be valid
        if (!payload.toPincode || !/^\d{6}$/.test(String(payload.toPincode))) {
            issues.push(`Shipment Pincode "${payload.toPincode}" must be a valid 6-digit pincode`);
        }

        // Item-level: HSN (4/8 digits), Unit (max 3 chars), Product/Description charset, Assessable Value
        (payload.itemList || []).forEach((item, idx) => {
            const rowLabel = `Item #${item.itemNo ?? idx + 1}`;

            if (item.qtyUnit && String(item.qtyUnit).length !== 3) {
                issues.push(`${rowLabel}: Unit "${item.qtyUnit}" should have only 3 characters`);
            }

            if (item.productName && !PRODUCT_CHARSET_REGEX.test(item.productName)) {
                issues.push(`${rowLabel}: Product name contains invalid characters (only alphanumeric and . - / allowed)`);
            }
            if (item.productDesc && !PRODUCT_CHARSET_REGEX.test(item.productDesc)) {
                issues.push(`${rowLabel}: Description contains invalid characters (only alphanumeric and . - / allowed)`);
            }

            if (item.taxableAmount === undefined || item.taxableAmount === null || Number.isNaN(item.taxableAmount)) {
                issues.push(`${rowLabel}: Assessable Value is required`);
            }
        });

        return issues;
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormState((prev) => ({ ...prev, [name]: value }));
        if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }));
    };

    const authorizeEway = async (gstin, username, password) => {
        // 🚫 TEMPORARILY DISABLED — payload-only mode, see generateEwayBill(). We only want to
        // inspect the E-Way Bill payload, not actually authorize/download an existing bill.
        // Re-enable this block to resume authorization.
        
        // const authUrl = `http://103.133.215.205:5000/api/v2/eway/authorize?gstin=${encodeURIComponent(gstin)}&username=${encodeURIComponent(username)}&ewbpwd=${encodeURIComponent(password)}`;
        const authUrl = `https://gstservice.reonbook.com/api/v2/eway/authorize?gstin=${encodeURIComponent(gstin)}&username=${encodeURIComponent(username)}&ewbpwd=${encodeURIComponent(password)}`;

        const authResponse = await axios.get(authUrl);

        const authtoken =
            authResponse.data?.data?.authtoken ||
            authResponse.data?.authtoken ||
            authResponse.data?.token ||
            (typeof authResponse.data === "string" ? authResponse.data : null);

        if (!authtoken) {
            throw new Error("Authorization failed. Token not received.");
        }

        return authtoken;
        

        throw new Error("E-Way Bill authorization is temporarily disabled. Payload-only mode.");
    };

    // Builds the exact payload object sent to the E-Way Bill generation API.
    // Extracted so both generateEwayBill() and the on-load validation preview use identical logic.
    const buildEwayBillPayload = () => {
        const vehicleNo = String(ewayData?.vehicleNo || "").trim();

        // Get distance based on transType
        let distanceToSend = formState.distance;
        if (formState.transType === "Regular" || formState.transType === "Bill From-Disaptch From") {
            distanceToSend = ewayData?.client?.details?.Distance || formState.distance;

        } else {
            distanceToSend = ewayData?.consigner?.shipTo?.Distance || formState.distance;
        }

        return {
            supplyType: String(supplyTypeMap[formState.supplyType]),
            subSupplyType: subTypeMap[formState.subType],
            subSupplyDesc: "",
            docType: String(docTypeMap[formState.docType]),
            docNo: String(formState.billNo),
            docDate: String(formatDateToDDMMYYYY(ewayData?.client?.inquiryDate)),
            fromGstin: String(ewayData?.company?.gstNo || "URP"),
            fromTrdName: String(ewayData?.company?.companyName || ""),
            fromAddr1: fromAddressObj.addr1,
            fromAddr2: fromAddressObj.addr2,
            fromPlace,
            fromPincode,
            actFromStateCode,
            fromStateCode: Number(ewayData?.company?.stateCode || ""),
            toGstin: String(ewayData?.client?.details?.["GST No."] || "URP"),
            toTrdName: String(ewayData?.client?.partyName || ""),
            toAddr1,
            toAddr2,
            toPlace,
            toPincode,
            toStateCode: Number(ewayData?.client?.details?.stateCode || ""),
            actToStateCode,
            transactionType: transTypeMap[formState.transType],
            dispatchFromGSTIN,
            dispatchFromTradeName,
            shipToGSTIN,
            shipToTradeName,
            otherVal: Number(ewayData?.product?.summary?.roundOff || 0),
            totalValue: Number(ewayData?.product?.summary?.basic || 0).toFixed(2),
            cgstValue: Number(ewayData?.product?.summary?.cgst || 0),
            sgstValue: Number(ewayData?.product?.summary?.sgst || 0),
            igstValue: Number(ewayData?.product?.summary?.igst || 0),
            cessValue: 0,
            cessNonAdvolValue: 0,
            totInvValue: Number(ewayData?.product?.summary?.total || 0),
            transporterId: String(ewayData?.transporter?.transporterGstNo || ""),
            transporterName: String(ewayData?.transporter?.transporterName || ""),
            transDocNo: "",
            transMode: vehicleNo === "" ? null : transModeMap[formState.transMode],
            transDistance: Number(distanceToSend || 0),
            transDocDate: "",
            vehicleNo: vehicleNo === "" ? null : vehicleNo,
            vehicleType: String(vehicleTypeMap[formState.vehicleType]),
            itemList: ewayData?.product?.tableData?.map((item, index) => ({
                itemNo: index + 1,
                productName: String(item.description || ""),
                productDesc: String(item.description || ""),
                hsnCode: String(item.hsnCode || ""),
                quantity: Number(item.qty),
                qtyUnit: String(item.unit || ""),
                cgstRate: Number(item.CGSTPercent || 0),
                sgstRate: Number(item.SGSTPercent || 0),
                igstRate: Number(item.IGSTPercent || 0),
                cessRate: 0,
                cessNonAdvol: 0,
                taxableAmount: Number(item.amount || 0).toFixed(2),
            })) || []
        };
    };

    // ✅ Proactive spec-validation warning — same pattern as the Vehicle No check in
    // fetchEwayBillData: shown as soon as invoice data loads, before the user clicks Generate.
    // Also disables the Generate button (via specValidationIssues) until the issues are resolved.
    useEffect(() => {
        if (!ewayData) return;

        const previewPayload = buildEwayBillPayload();
        console.log("EwayBill payload (preview):", JSON.stringify(previewPayload, null, 2));
        const issues = validateEwaySpecFields(previewPayload);

        if (issues.length > 0) {
            setSpecValidationIssues(issues);
            setSubmitError(`E-Way Bill data has the following issues:\n${issues.map((i) => `• ${i}`).join('\n')}`);
            setSnackbar({ open: true, severity: "warning", message: `E-Way Bill data has ${issues.length} issue${issues.length > 1 ? 's' : ''}. Generate button disabled until fixed.` });
        } else {
            setSpecValidationIssues([]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ewayData, formState.transType, formState.dispatchState, formState.shipToState]);

    const generateEwayBill = async () => {

        const ewayBillPayload = buildEwayBillPayload();

        console.log("eway Payload send: ", ewayBillPayload);

        // ✅ Validate the requested E-Way Bill spec fields before hitting the external API
        const specIssues = validateEwaySpecFields(ewayBillPayload);
        if (specIssues.length > 0) {
            console.warn("E-Way Bill spec validation failed:", specIssues);
            const error = new Error(specIssues.join(" | "));
            error.specIssues = specIssues;
            throw error;
        }

        // 🚫 TEMPORARILY DISABLED — actual generation call to the E-Way Bill server.
        // Payload-only mode: we only want to inspect ewayBillPayload above (see console.log),
        // not actually generate/store a real E-Way Bill. Re-enable this block to resume generation.
        
        let response;
        try {
            response = await axios.post(
                // 'http://103.133.215.205:5000/api/v2/eway/generate-with-auth',
                'https://gstservice.reonbook.com/api/v2/eway/generate-with-auth',
                ewayBillPayload,
                {
                    params: {
                        username: formState.EWBUserName,
                        ewbpwd: formState.EWBPassword,
                        gstin: formState.gstNo,
                        server: ewayBillServer
                    }
                }
            );
        } catch (err) {
            // ✅ Axios throws on non-2xx status, which skips any code AFTER the request —
            // so this log is the only place we can see what the server actually rejected with.
            console.error("E-Way Bill Server Error Response (raw):", err.response);
            console.error("E-Way Bill Server Error Response (data):", err.response?.data);
            throw new Error(err.response?.data?.message || err.message || "E-Way Bill generation failed");
        }

        // ✅ LOG RAW RESPONSE FROM E-WAY BILL SERVER
        console.log("E-Way Bill Server Response (raw):", response);
        console.log("E-Way Bill Server Response (data):", response.data?.data);

        if (!response.data?.success || !response.data?.data?.ewayBillNo) {
            console.log("E-Way Bill Server Response (raw)1:", response);
            throw new Error(response.data?.message || "E-Way Bill generation failed");
        }

        return response.data.data;
        // 🚫 Stop here in payload-only mode — nothing below this point runs.
        throw new Error("E-Way Bill generation is temporarily disabled. Payload logged above only.");
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        setSubmitError('');
        setEwayBillError('');
        setEwayBillSuccess(false);
        setEwayBillLoading(true);

        if (specValidationIssues.length > 0) {
            setSubmitError(`E-Way Bill data has the following issues:\n${specValidationIssues.map((i) => `• ${i}`).join('\n')}`);
            setEwayBillLoading(false);
            return;
        }

        const fieldErrors = validate();
        if (Object.keys(fieldErrors).length > 0) {
            const messages = Object.values(fieldErrors).filter(Boolean);
            setSubmitError(`Please fix the following:\n${messages.map((m) => `• ${m}`).join('\n')}`);
            setEwayBillLoading(false);
            return;
        }

        const gstin = formState.gstNo;
        const username = formState.EWBUserName;
        const password = formState.EWBPassword;

        try {
            let authtoken;
            let ewayNo;
            let ewayDate;

            // 🔵 CASE 1: EWB ALREADY EXISTS
            if (ewayExists && formState.ewayBillNo) {
                authtoken = await authorizeEway(gstin, username, password);
                ewayNo = formState.ewayBillNo;
            } else {

                // 🟢 CASE 2: GENERATE
                const generated = await generateEwayBill();

                // ✅ FIX: The wrapper API actually returns { authtoken, ewayBillNo, server, raw: { ewayBillNo, ewayBillDate, validUpto, alert } } —
                // ewayBillDate only exists NESTED inside "raw", not at the top level, so the previous
                // fallback chain (which only checked top-level keys) always got undefined for the date,
                // causing /api/store-eway to reject with 400 "EwayBillNo and EwayBillDate are required".
                // generated.raw?.ewayBillDate / generated.raw?.validUpto added as the real source of the date.
                ewayNo = generated.ewayBillNo || generated.ewayNo || generated.EwayBillNo;
                ewayDate = generated.ewayBillDate || generated.ewayDate || generated.EwayBillDate
                    || generated.raw?.ewayBillDate || generated.raw?.validUpto || generated.validUpto;
                authtoken = generated.authtoken;

                console.log(`ewayBillNo: ${ewayNo} and ewayBillDate: ${ewayDate}`)
                setFormState(prev => ({ ...prev, ewayBillNo: String(ewayNo) }));

                // 🚫 TEMPORARILY DISABLED — STORE (payload-only mode, see generateEwayBill()).
                // Unreachable anyway since generateEwayBill() now throws before returning, kept
                // commented for clarity in case the throw above is ever removed independently.
                await axios.post(`${connectionString}/api/store-eway`, {
                    companyId,
                    bookName,
                    userId: user?.UserId,
                    invoiceId,
                    branchId: user?.BranchId || "",
                    fYear: user?.FYear || "",
                    ewayBillNo: ewayNo,
                    ewayBillDate: ewayDate
                });

                // 🚫 COMMENTED — no matching backend route/handler exists (404), aborted the
                // flow before the download step below could run. Re-enable once a real
                // /update-eway-master route + controller function is added on the backend.
                // await axios.post(`${connectionString}/api/update-eway-master`, {
                //     companyId,
                //     userId: user?.UserId,
                //     bookName,
                //     invoiceId,
                //     ewayBillNo: ewayNo,
                //     ewayBillDate: ewayDate
                // });

                setEwayBillSuccess(true);
                setSnackbar({ open: true, message: 'E-Way Bill generated and stored successfully', severity: 'success' });
            }
            // ✅ COMMON STEP — DOWNLOAD
            await handleDownload( authtoken, gstin, username, ewayNo );
        } catch (err) {

            if (err.specIssues && err.specIssues.length > 0) {
                setSubmitError(`E-Way Bill validation failed:\n${err.specIssues.map((i) => `• ${i}`).join('\n')}`);
                setEwayBillError(err.specIssues.join(' | '));
                setSnackbar({
                    open: true,
                    message: `E-Way Bill validation failed (${err.specIssues.length} issue${err.specIssues.length > 1 ? 's' : ''}). See details below.`,
                    severity: "error"
                });
            } else {
                const backendMessage = err.response?.data?.message || err.message || "E-Way Bill process failed";
                setEwayBillError(backendMessage);
                setSnackbar({ open: true, message: backendMessage, severity: "error" });
            }

        } finally {
            setEwayBillLoading(false);
        }
    };

    const handleDownload = async ( authtokenParam, gstinParam, usernameParam, ewbNoParam ) => {
        try {
            const gstin = gstinParam || formState.gstNo;
            const username = usernameParam || formState.EWBUserName;
            const ewbNo = ewbNoParam || formState.ewayBillNo;
            const authtoken = authtokenParam;

            if (!ewbNo || !authtoken) {
                setSnackbar({ open: true, message: "Missing token or EWB number", severity: "error" });
                return;
            }

            const response = await axios.get(
                // `http://103.133.215.205:5000/api/v2/eway/details-print?gstin=${encodeURIComponent(gstin)}&username=${encodeURIComponent(username)}&authtoken=${encodeURIComponent(authtoken)}&ewbNo=${encodeURIComponent(ewbNo)}`,
                `https://gstservice.reonbook.com/api/v2/eway/details-print?gstin=${encodeURIComponent(gstin)}&username=${encodeURIComponent(username)}&authtoken=${encodeURIComponent(authtoken)}&ewbNo=${encodeURIComponent(ewbNo)}`,
                {
                    responseType: "json" // Change from arraybuffer to json
                }
            );

            // Check if the response has a base64 PDF
            if (response.data.success && response.data.data.pdfBase64) {
                // Remove the data URL prefix if it exists
                const base64PDF = response.data.data.pdfBase64.replace(/^data:application\/pdf;base64,/, '');

                // Convert base64 to blob
                const blob = base64ToBlob(base64PDF, 'application/pdf');
                const blobUrl = window.URL.createObjectURL(blob);

                window.open(blobUrl, "_blank");

                setSnackbar({ open: true, message: "E-Way Bill opened successfully", severity: "success" });
            } else {
                throw new Error("PDF data not found in response");
            }

        } catch (error) {
            setSnackbar({ open: true, message: error.response?.data?.message || "Failed to download E-Way Bill", severity: "error" });
        }
    };

    const handleReset = () => {
        setFormState({ EWBUserName: '', EWBPassword: '', partyName: '', billNo: '', ewayBillNo: '', supplyType: 'Outward', subType: 'Supply', docType: 'Tax Invoice', transMode: 'Road', dispatchState: 'Combo1', shipToState: 'Combo1', vehicleType: 'Regular', distance: '', transType: 'Regular', });
        setErrors({});
        setSubmitError('');
        setEwayBillError('');
        setEwayBillSuccess(false);
        setSnackbar({ open: true, message: 'Form cleared', severity: 'info' });
    };

    const renderTextField = (label, name, required = false, multiline = false, disabled = false, type = 'text') => (
        <TextField fullWidth label={label} name={name} value={formState[name] || ''} onChange={handleChange} error={!!errors[name]} helperText={errors[name]} required={required} multiline={multiline} minRows={multiline ? 4 : 1} disabled={disabled} type={type} variant="outlined" size="small" sx={{ '& .MuiOutlinedInput-root': { fontSize: '0.9rem' }, '& .MuiInputLabel-root': { fontSize: '0.85rem' }, }} />
    );

    const renderSelect = (label, name, options) => (
        <FormControl fullWidth size="small">
            <InputLabel sx={{ fontSize: '0.85rem' }}>{label}</InputLabel>
            <Select name={name} value={formState[name] || options[0]} label={label} onChange={handleChange} >
                {options.map((opt) => (
                    <MenuItem key={opt} value={opt}> {opt} </MenuItem>
                ))}
            </Select>
        </FormControl>
    );

    return (
        <Box sx={{ bgcolor: 'white', p: 1.5, fontFamily: 'Arial, sans-serif' }}>
            <Paper elevation={0} sx={{ border: '1px solid #ccc', maxWidth: 700, mx: 'auto', overflow: 'hidden', bgcolor: '#fffef5', }} >
                {/* Header */}
                {/* <Box sx={{ bgcolor: '#fffacd', p: 1.5, borderBottom: '2px solid #d4a574', textAlign: 'center' }}>
                     <Typography variant="h6" fontWeight="bold" color="#000"> E-WAY BILL SYSTEM LOGIN </Typography> 
                </Box> */}

                <Box sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {/* <Box sx={{ maxWidth: 1100, mx: 'auto', px: 2, py: 1 }}>
                      
                            <Grid container spacing={1.5} justifyContent="center">
                                <Grid item xs={12} sm={6}>
                                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                        <Typography variant="subtitle2" sx={{ fontSize: '0.85rem', mb: 0.5, fontWeight: 500 }}> EWB Username </Typography>
                                        {renderTextField('', 'EWBUserName', true, false, true)}
                                    </Box>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                        <Typography variant="subtitle2" sx={{ fontSize: '0.85rem', mb: 0.5, fontWeight: 500 }}> EWB Password </Typography>
                                        {renderTextField('', 'EWBPassword', true, false, true, 'password')}
                                    </Box>
                                </Grid>
                            </Grid>

                            <Divider sx={{ my: 2.5 }} />
                     
                            <Grid container spacing={1.5} justifyContent="center">
                                <Grid item xs={12} sm={4}>
                                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                        <Typography variant="subtitle2" sx={{ fontSize: '0.85rem', mb: 0.5, fontWeight: 500 }}>  Party Name </Typography>
                                        {renderTextField('', 'partyName', true, false, true)}
                                    </Box>
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                        <Typography variant="subtitle2" sx={{ fontSize: '0.85rem', mb: 0.5, fontWeight: 500 }}> Bill No </Typography>
                                        {renderTextField('', 'billNo', true, false, true)}
                                    </Box>
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                        <Typography variant="subtitle2" sx={{ fontSize: '0.85rem', mb: 0.5, fontWeight: 500 }}> EwayBillNo </Typography>
                                        {renderTextField('', 'ewayBillNo', false, false, true)}
                                    </Box>
                                </Grid>
                            </Grid>

                      
                            <Grid container spacing={1.5} justifyContent="center" sx={{ mt: 2 }}>
                                <Grid item xs={12} sm={3}>
                                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                        <Typography variant="subtitle2" sx={{ fontSize: '0.85rem', mb: 0.5, fontWeight: 500 }}> Supply Type </Typography>
                                        {renderSelect('', 'supplyType', supplyTypeOptions)}
                                    </Box>
                                </Grid>
                                <Grid item xs={12} sm={3}>
                                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                        <Typography variant="subtitle2" sx={{ fontSize: '0.85rem', mb: 0.5, fontWeight: 500 }}> Sub Type </Typography>
                                        {renderSelect('', 'subType', subTypeOptions)}
                                    </Box>
                                </Grid>
                                <Grid item xs={12} sm={3}>
                                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                        <Typography variant="subtitle2" sx={{ fontSize: '0.85rem', mb: 0.5, fontWeight: 500 }}> Doc Type </Typography>
                                        {renderSelect('', 'docType', docTypeOptions)}
                                    </Box>
                                </Grid>
                                <Grid item xs={12} sm={3}>
                                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                        <Typography variant="subtitle2" sx={{ fontSize: '0.85rem', mb: 0.5, fontWeight: 500 }}> Trans Mode </Typography>
                                        {renderSelect('', 'transMode', transModeOptions)}
                                    </Box>
                                </Grid>
                            </Grid>

                     
                            <Grid container spacing={1.5} justifyContent="center" sx={{ mt: 2 }}>
                                <Grid item xs={12} sm={6} md={2.4}>
                                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                        <Typography variant="subtitle2" sx={{ fontSize: '0.85rem', mb: 0.5, fontWeight: 500 }}> Dispatch State </Typography>
                                        {renderSelect('', 'dispatchState', STATE_OPTIONS)}
                                    </Box>
                                </Grid>
                                <Grid item xs={12} sm={6} md={2.4}>
                                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                        <Typography variant="subtitle2" sx={{ fontSize: '0.85rem', mb: 0.5, fontWeight: 500 }}> Ship To State </Typography>
                                        {renderSelect('', 'shipToState', STATE_OPTIONS)}
                                    </Box>
                                </Grid>
                                <Grid item xs={12} sm={6} md={2.4}>
                                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                        <Typography variant="subtitle2" sx={{ fontSize: '0.85rem', mb: 0.5, fontWeight: 500 }}> Vehicle Type </Typography>
                                        {renderSelect('', 'vehicleType', vehicleTypeOptions)}
                                    </Box>
                                </Grid>
                                <Grid item xs={12} sm={6} md={2.4}>
                                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                        <Typography variant="subtitle2" sx={{ fontSize: '0.85rem', mb: 0.5, fontWeight: 500 }}> Distance (Km) </Typography>
                                        {renderTextField('', 'distance')}
                                    </Box>
                                </Grid>
                                <Grid item xs={12} sm={6} md={2.4}>
                                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                        <Typography variant="subtitle2" sx={{ fontSize: '0.85rem', mb: 0.5, fontWeight: 500 }}> TransType </Typography>
                                        {renderSelect('', 'transType', transTypeOptions)}
                                    </Box>
                                </Grid>
                            </Grid>
                        </Box> */}

                        <Box sx={{ mt: 2.5 }}>
                            {/* <Divider sx={{ mb: 2 }} /> */}

                            <FormControl size="small" sx={{ minWidth: 160, mb: 2 }}>
                                <InputLabel id="ewaybill-server-label">Server</InputLabel>
                                <Select
                                    labelId="ewaybill-server-label"
                                    label="Server"
                                    value={ewayBillServer}
                                    onChange={(e) => setEwayBillServer(e.target.value)}
                                >
                                    <MenuItem value="Primary">Primary</MenuItem>
                                    <MenuItem value="Backup1">Backup1</MenuItem>
                                    <MenuItem value="Backup2">Backup2</MenuItem>
                                </Select>
                            </FormControl>

                            <Typography variant="subtitle2" sx={{ fontSize: '0.9rem', mb: 1, fontWeight: 600 }}> Get EWB Details / Paste EWB No for Print/Etc </Typography>

                            {/* Show Error or Success Message in TextField */}
                            <Box sx={{ position: 'relative', mb: 2 }}>
                                {renderTextField(ewayBillError ? `Error: ${ewayBillError}` : 'E-Way Bill No', 'ewayBillNo', false, true, true, 'text')}
                                {ewayBillError && (
                                    <Typography variant="caption" sx={{ color: '#d32f2f', mt: 0.5, display: 'block' }}> {ewayBillError} </Typography>
                                )}
                            </Box>

                            {/* Action Buttons */}
                            <Box sx={{ mt: 2.5, display: 'flex', flexWrap: 'wrap', gap: 1.5, justifyContent: 'center' }}>
                                {/* {!ewayExists && ( */}
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        size="small"
                                        onClick={handleSubmit}
                                        disabled={ewayBillLoading || Object.keys(vehicleValidationErrors).length > 0 || specValidationIssues.length > 0}
                                        sx={{ minWidth: 140 }}
                                    >
                                        {ewayBillLoading ? (
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <CircularProgress size={16} color="inherit" />
                                                Generating...
                                            </Box>
                                        ) : (
                                            'Generate E way Bill'
                                        )}
                                    </Button>
                                {/* )} */}

                                {/* Show Download button only if E-Way Bill is successfully generated */}
                                {(ewayBillSuccess || ewayExists) && formState.ewayBillNo && (
                                    <Button variant="contained" color="success" size="small" startIcon={<GetApp />} onClick={handleSubmit} sx={{ minWidth: 160 }} > Download EWayBill </Button>
                                )}
                            </Box>
                        </Box>
                    </Box>
                </Box>
            </Paper >

            {/* Snackbar */}
            < Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} >
                <Alert severity={snackbar.severity} variant="filled" sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar >

            {submitError && (
                <Alert severity="error" sx={{ mt: 1.5, maxWidth: 1200, mx: 'auto', whiteSpace: 'pre-line' }}>
                    {submitError}
                </Alert>
            )}
        </Box >
    );
};

export default EwayBillMaster;